import { Component, OnInit } from '@angular/core';
import { OperationError } from '../../../models/server-result/operation-error';
import { CodeDecodeValue } from '../../../models/common/code-decode-value';
import { ServerApiService } from '../../../server-api/server-api.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LogService } from '../../../services/log/log.service';
import { RegisterUserInput } from '../../../models/User/register-user-input';
import { HttpErrorResponse } from '@angular/common/http';
import { OperationResult } from '../../../models/server-result/operation-result';
import { TranslateService } from '../../../services/translate/translate.service';
import { AppContextService } from '../../../services/app-context.service';
import { forkJoin, Observable } from 'rxjs';
import { UtilitiesService } from '../../../services/utils/utilities.service';
import { TimeZoneValue } from '../../../models/common/time-zone-value';
import { PublicUserInvitationData } from '../../../models/invitations/public-user-invitation-data';
import { CompanyLegalAcceptance } from '../../../models/funding-request/company-legal-acceptance';
import { InvitationToken } from '../../../models/invitations/token';
import { PublicCompany } from '../../../models/companies/public-company';
import { InvitationsApiService } from '../../../server-api/invitations-api.service';
import { PersonalDataConsentLinkAcceptance } from '../../../server-api/user-api/user-api.models';

@Component({
	selector: 'app-user-invitation-response',
	templateUrl: './user-invitation-response.component.html',
	styleUrls: [],
	standalone: false,
})
export class UserInvitationResponseComponent implements OnInit {
	public isPerformingRequest = false;
	public isSubmittingAccept = false;
	public isSubmittingRefuse = false;
	public requestErrors: OperationError[] = [];

	public languageCodes: CodeDecodeValue[];
	public timeZones: TimeZoneValue[];

	private userCompanyId: string;
	private invitingCompanyId: string;
	public invitedUserEmail: string;

	public registerUserInput: RegisterUserInput = new RegisterUserInput();
	public invitingCompany: PublicCompany = new PublicCompany();

	public showSucessSubmission: boolean = false;
	public sucessSubmissionContent: string | null = null;

	public newUser: boolean = false;
	public token = new InvitationToken();

	private personalDataConsentDocumentAcceptance: PersonalDataConsentLinkAcceptance | null = null;
	private legalUserAcceptance: CompanyLegalAcceptance | null = null;
	public pageDisplayLanguageCode: string | null = null;

	constructor(
		private serverApiService: ServerApiService,
		private toasterService: ToasterService,
		private route: ActivatedRoute,
		private router: Router,
		private logger: LogService,
		private translateService: TranslateService,
		private appContext: AppContextService,
		private utilitiesService: UtilitiesService,
		private invitationsApiService: InvitationsApiService
	) {}

	ngOnInit() {
		this.pageDisplayLanguageCode = this.appContext.getApplicationLanguageCode();

		this.route.params.subscribe((params) => {
			let base64InvitationContext = params['invitationContext'];

			if (!base64InvitationContext) {
				this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_INVITATION_CONTEXT_FROM_ROUTE', 'list-errorCodes'));
				return;
			}

			let invitationContext = atob(base64InvitationContext);

			if (!!invitationContext) {
				let parameters: string[] = invitationContext.split('&');

				if (parameters.length != 4) {
					this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_INVITATION_CONTEXT_CONTENT', 'list-errorCodes'));
				} else {
					this.token.Id = parameters[0].split(':')[1];
					this.userCompanyId = parameters[1].split(':')[1];
					this.invitingCompanyId = parameters[2].split(':')[1];
					this.invitedUserEmail = parameters[3].split(':')[1];
					this.token.relatedEmail = this.invitedUserEmail;

					if (!!this.token.Id && !!this.userCompanyId && !!this.invitingCompanyId && !!this.invitedUserEmail) {
						this.isPerformingRequest = true;
						const checkIfInvitationtTokenExpiredServiceCall = this.invitationsApiService.getUserInvitation(
							this.invitedUserEmail,
							this.userCompanyId,
							this.token.Id
						);
						const getTimeZonesServiceCall = this.serverApiService.getTimeZones();
						const getLanguageCodesServiceCall = this.serverApiService.getLanguageCodes();

						let servicesCallsToMake: Array<Observable<OperationResult<PublicUserInvitationData | TimeZoneValue[] | CodeDecodeValue[]>>> =
							[];

						servicesCallsToMake.push(checkIfInvitationtTokenExpiredServiceCall);
						servicesCallsToMake.push(getTimeZonesServiceCall);
						servicesCallsToMake.push(getLanguageCodesServiceCall);
						const getAllRequests = forkJoin(servicesCallsToMake);

						getAllRequests.subscribe({
							next: (opResults) => {
								const getUserInvitationTicketResult = opResults[0] as OperationResult<PublicUserInvitationData>;
								const getTimeZonesResult = opResults[1] as OperationResult<TimeZoneValue[]>;
								const getLanguageCodesResult = opResults[2] as OperationResult<CodeDecodeValue[]>;

								if (!getUserInvitationTicketResult.IsValid) {
									this.processInvalidResult(getTimeZonesResult);
									this.isPerformingRequest = false;
									return;
								}

								if (!getTimeZonesResult.IsValid) {
									this.processInvalidResult(getTimeZonesResult);
									this.isPerformingRequest = false;
									return;
								}

								if (!getLanguageCodesResult.IsValid) {
									this.processInvalidResult(getTimeZonesResult);
									this.isPerformingRequest = false;
									return;
								}

								if (getUserInvitationTicketResult.Data.TokenExpired) {
									this.token.expired = getUserInvitationTicketResult.Data.TokenExpired;
									this.showInvitationContextInformationWhenTokenUsedOrExpired(getUserInvitationTicketResult.Data);
									this.retrivedTokenAsExpiredMessages();
									this.isPerformingRequest = false;
									return;
								}

								if (getUserInvitationTicketResult.Data.TokenUsed) {
									this.token.used = getUserInvitationTicketResult.Data.TokenUsed;
									this.showInvitationContextInformationWhenTokenUsedOrExpired(getUserInvitationTicketResult.Data);
									this.retrivedTokenUsedMessages();
									this.isPerformingRequest = false;
									return;
								}

								this.newUser = !getUserInvitationTicketResult.Data.InvitedExistingUser;
								this.languageCodes = getLanguageCodesResult.Data;
								this.timeZones = this.utilitiesService.GetOrderedTimeZoneValues(getTimeZonesResult.Data, false);

								this.invitingCompany = <PublicCompany>{
									Name: getUserInvitationTicketResult.Data.InvitingCompanyName,
									TimeZone: getUserInvitationTicketResult.Data.InvitingCompanyTimeZone,
									LanguageCode: getUserInvitationTicketResult.Data.InvitingCompanyLanguageCode,
								};

								this.registerUserInput.Email = this.invitedUserEmail;
								this.registerUserInput.TimeZone = this.invitingCompany.TimeZone;
								this.registerUserInput.InvitingCompanyId = this.invitingCompanyId;
								this.registerUserInput.UserCompanyId = this.userCompanyId;

								if (!!getUserInvitationTicketResult.Data.InvitedExistingUser) {
									this.registerUserInput.LanguageCode = getUserInvitationTicketResult.Data.InvitedExistingUser.LanguageCode;
								} else if (!!this.invitingCompany.LanguageCode) {
									this.registerUserInput.LanguageCode = this.invitingCompany.LanguageCode;
								}

								this.pageDisplayLanguageCode = this.registerUserInput.LanguageCode;
							},
							error: (httpErrorResponse: HttpErrorResponse) => {
								this.isPerformingRequest = false;
								this.logger.error(
									'[UserInvitationResponseComponent::checkIfInvitationtTokenExpiredServiceCall] HttpErrorResponse: ',
									httpErrorResponse
								);
								this.toasterService.showError(
									'[UserInvitationResponseComponent::checkIfInvitationtTokenExpiredServiceCall] HttpErrorResponse: ' +
										httpErrorResponse.error +
										httpErrorResponse.message
								);
							},
						});
					} else {
						this.toasterService.showError(
							this.translateService.translate('COULD_NOT_GET_INVITATION_CONTEXT_PARAMETERS', 'list-errorCodes')
						);
						this.router.navigate(['/']);
					}
				}
			}
		});
	}

	/**
	 * Handle document updates from invitation-screen component
	 * This is called on initial load AND when user changes language
	 */
	public onDocumentsUpdated(documents: {
		legalAcceptance: CompanyLegalAcceptance;
		personalDataConsent: PersonalDataConsentLinkAcceptance;
	}): void {
		this.legalUserAcceptance = documents.legalAcceptance;
		this.personalDataConsentDocumentAcceptance = documents.personalDataConsent;
	}

	public onPageDisplayLanguageCodeChange(languageCode: string): void {
		this.pageDisplayLanguageCode = languageCode;
	}

	public onAcceptInvitation(event: { registerUserInput: RegisterUserInput }): void {
		if (event.registerUserInput) {
			this.appContext.setApplicationLanguageCode(event.registerUserInput.LanguageCode);
		}
		this.registerUserInput = event.registerUserInput;
		this.registerUserInput.UserCompanyId = this.userCompanyId;
		this.registerUserInput.Token = this.token.Id;
		this.registerUserInput.TokenRelatedEmail = this.token.relatedEmail || this.invitedUserEmail;
		this.registerUserInput.Username = this.registerUserInput.Email;
		this.registerUserInput.InvitingCompanyId = this.invitingCompanyId;
		this.isSubmittingAccept = true;

		this.registerUserInput.TermsAndConditionsDocumentAcceptanceHash = this.legalUserAcceptance!.AcceptanceHash;
		this.registerUserInput.TermsAndConditionsDocumentId = this.legalUserAcceptance!.DocumentId;
		this.registerUserInput.PersonalDataConsentDocumentId = this.personalDataConsentDocumentAcceptance!.DocumentId;

		this.registerUserInput.InvitationLanguageCode = this.pageDisplayLanguageCode!;

		const registerUserServiceCall = this.serverApiService.registerUser(this.registerUserInput);

		registerUserServiceCall.subscribe({
			next: (operationResultLocal: OperationResult<boolean>) => {
				this.isSubmittingAccept = false;
				if (operationResultLocal.IsValid && operationResultLocal.Data) {
					this.processValidRegisterUserResult();
				} else {
					this.processInvalidRegisterUserResult(operationResultLocal);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isSubmittingAccept = false;
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	public onRefuseInvitation(event: { registerUserInput: RegisterUserInput }): void {
		if (event.registerUserInput) {
			this.appContext.setApplicationLanguageCode(event.registerUserInput.LanguageCode);
		}
		this.isSubmittingRefuse = true;

		setTimeout(() => {
			this.isSubmittingRefuse = false;
			this.router.navigate(['/public/login']);
		}, 500);
	}

	private processValidRegisterUserResult(): void {
		this.sucessSubmissionContent = this.translateService
			.translateByLanguage('user-invitation-response.modalInformationContent', this.pageDisplayLanguageCode!, 'client')
			.replace('[Email]', this.registerUserInput.Username);
		this.showSucessSubmission = true;
	}

	private processInvalidRegisterUserResult(operationResult: OperationResult<boolean>): void {
		operationResult.Errors?.forEach((e) =>
			this.toasterService.showError(this.translateService.translateByLanguage(e.Code, this.invitingCompany.LanguageCode, 'list-errorCodes'))
		);
	}

	private retrivedTokenAsExpiredMessages(): void {
		this.token.expiredContent = this.translateService.translateByLanguage(
			'user-invitation-response.modalInformationTokenAsExpiredContent',
			this.pageDisplayLanguageCode!,
			'client'
		);
	}

	private retrivedTokenUsedMessages(): void {
		this.token.usedContent = this.translateService.translateByLanguage(
			'user-invitation-response.modalInformationTokenUsedContent',
			this.pageDisplayLanguageCode!,
			'client'
		);
	}

	private showInvitationContextInformationWhenTokenUsedOrExpired(publicUserInvitationData: PublicUserInvitationData): void {
		if (!!publicUserInvitationData) {
			this.registerUserInput.TimeZone = publicUserInvitationData.InvitingCompanyTimeZone;

			if (!!publicUserInvitationData.InvitedExistingUser) {
				this.registerUserInput.LanguageCode = publicUserInvitationData.InvitedExistingUser.LanguageCode;
			} else if (!!publicUserInvitationData.InvitingCompanyLanguageCode) {
				this.registerUserInput.LanguageCode = publicUserInvitationData.InvitingCompanyLanguageCode;
			}

			this.pageDisplayLanguageCode = this.registerUserInput.LanguageCode;

			// Set up inviting company info
			this.invitingCompany = <PublicCompany>{
				Name: publicUserInvitationData.InvitingCompanyName,
				TimeZone: publicUserInvitationData.InvitingCompanyTimeZone,
				LanguageCode: publicUserInvitationData.InvitingCompanyLanguageCode,
			};
		}
	}

	private processInvalidResult<T>(operationResult: OperationResult<T>) {
		const errors = operationResult.Errors;
		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}
}
